import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { MatIcon } from '@angular/material/icon';

/**
 * Describes a single context chip. Built by the host (e.g. the conversation
 * header) from a message's qualification fields.
 */
export interface ContextChipData {
  /** Text shown in the chip; either the value or an "empty" placeholder label. */
  label: string;
  /** Renders the chip in its muted, placeholder style. */
  empty?: boolean;
  /** Renders the chip with a solid dark background (used for the insured person). */
  filled?: boolean;
  /** Material icon name used as the leading element (lowest precedence). */
  icon?: string;
  /** Initials shown in a colored avatar (highest precedence). */
  avatarText?: string;
  /** Background color of the avatar. */
  avatarColor?: string;
  /** Color of the leading dot (used for the category). */
  dotColor?: string;
}

type ChipLeading = 'avatar' | 'dot' | 'icon' | 'none';

/**
 * Presentational, display-only chip. Interactive dropdown chips (assignee,
 * status) are a separate concern and reuse the shared chip-shell styles.
 */
@Component({
  selector: 'app-context-chip',
  imports: [MatIcon],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './context-chip.component.html',
  styleUrl: './context-chip.component.scss'
})
export class ContextChipComponent {
  readonly label = input.required<string>();
  readonly empty = input(false);
  readonly filled = input(false);
  readonly icon = input<string | undefined>(undefined);
  readonly avatarText = input<string | undefined>(undefined);
  readonly avatarColor = input<string | undefined>(undefined);
  readonly dotColor = input<string | undefined>(undefined);

  readonly leading = computed<ChipLeading>(() => {
    if (this.avatarText()) {
      return 'avatar';
    }
    if (this.dotColor()) {
      return 'dot';
    }
    if (this.icon()) {
      return 'icon';
    }
    return 'none';
  });
}
